import React, { useState, useEffect } from 'react';
import { Bio } from '../types';
import {
  StepContainer,
  StepTitle,
  StepDescription,
  BioContainer,
  BioEditor,
  BioTextarea,
  BioCounter,
  BioSuggestions,
  SuggestionItem,
  BioTips,
  TipItem,
  CharacterCount,
  WordCount,
  ValidationMessage,
  BioPreview
} from '../../../styles/Profile/ProfileSetup.styles';

interface BioStepProps {
  data: Partial<Bio>;
  onChange: (data: Bio, isValid: boolean) => void;
  onNext: () => void;
  onBack: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}

const MAX_CHARACTERS = 500;
const MIN_CHARACTERS = 50;
const MAX_WORDS = 100;

const BIO_SUGGESTIONS = [
  "I love exploring new places and trying different cuisines",
  "Passionate about fitness and outdoor adventures",
  "Looking for someone to share life's little moments with",
  "I enjoy reading, hiking, and good conversation",
  "Seeking a genuine connection with someone special",
  "Love traveling, photography, and making memories",
  "I'm a foodie who loves cooking and trying new restaurants",
  "Looking for someone who shares my love for music and art",
  "I enjoy staying active and trying new sports",
  "Seeking someone to laugh with and create adventures together"
];

const BIO_TIPS = [
  "Be authentic and genuine in your description",
  "Include your interests and hobbies",
  "Mention what you're looking for in a relationship",
  "Keep it positive and upbeat",
  "Avoid clichés and generic statements",
  "Show your personality through your words",
  "Be specific about your interests",
  "Include what makes you unique"
];

export const BioStep: React.FC<BioStepProps> = ({
  data,
  onChange,
  onNext,
  onBack,
  isFirstStep,
  isLastStep
}) => {
  const [bio, setBio] = useState<Bio>({
    content: '',
    characterCount: 0,
    maxCharacters: MAX_CHARACTERS,
    lastUpdated: new Date(),
    ...data
  });

  const [wordCount, setWordCount] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const countWords = (text: string): number => {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const validateBio = (content: string): string[] => {
    const errors: string[] = [];
    
    if (content.length < MIN_CHARACTERS) {
      errors.push(`Bio must be at least ${MIN_CHARACTERS} characters long`);
    }
    
    if (content.length > MAX_CHARACTERS) {
      errors.push(`Bio must be no more than ${MAX_CHARACTERS} characters`);
    }
    
    const words = countWords(content);
    if (words > MAX_WORDS) {
      errors.push(`Bio must be no more than ${MAX_WORDS} words`);
    }
    
    if (words < 10) {
      errors.push('Bio should be at least 10 words');
    }
    
    return errors;
  };

  const handleBioChange = (content: string) => {
    const characterCount = content.length;
    const wordCount = countWords(content);
    const errors = validateBio(content);
    
    const updatedBio: Bio = {
      ...bio,
      content,
      characterCount,
      lastUpdated: new Date()
    };
    
    setBio(updatedBio);
    setWordCount(wordCount);
    setValidationErrors(errors);
    
    const isValid = errors.length === 0;
    onChange(updatedBio, isValid);
  };

  const handleSuggestionClick = (suggestion: string) => {
    const newContent = bio.content + (bio.content ? ' ' : '') + suggestion;
    handleBioChange(newContent);
    setShowSuggestions(false);
  };

  const getCharacterCountColor = (): string => {
    const percentage = (bio.characterCount / MAX_CHARACTERS) * 100;
    if (percentage >= 90) return '#ff4757';
    if (percentage >= 75) return '#ffa502';
    return '#2ed573';
  };

  const getWordCountColor = (): string => {
    if (wordCount > MAX_WORDS) return '#ff4757';
    if (wordCount < 10) return '#ffa502';
    return '#2ed573';
  };

  return (
    <StepContainer>
      <StepTitle>Write your bio</StepTitle>
      <StepDescription>
        Tell others about yourself! A good bio helps people get to know you better and increases your chances of making meaningful connections.
      </StepDescription>

      <BioContainer>
        <BioEditor>
          <BioTextarea
            value={bio.content}
            onChange={(e) => handleBioChange(e.target.value)}
            placeholder="Tell us about yourself... What are your interests? What are you looking for? What makes you unique?"
            rows={8}
            maxLength={MAX_CHARACTERS}
          />
          
          <BioCounter>
            <CharacterCount color={getCharacterCountColor()}>
              {bio.characterCount}/{MAX_CHARACTERS} characters
            </CharacterCount>
            <WordCount color={getWordCountColor()}>
              {wordCount} words
            </WordCount>
          </BioCounter>
        </BioEditor>

        {showSuggestions && (
          <BioSuggestions>
            <h4>Need inspiration? Try these:</h4>
            {BIO_SUGGESTIONS.map((suggestion, index) => (
              <SuggestionItem
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
              >
                {suggestion}
              </SuggestionItem>
            ))}
          </BioSuggestions>
        )}

        {bio.content.length < MIN_CHARACTERS && (
          <button
            onClick={() => setShowSuggestions(!showSuggestions)}
            style={{
              background: 'transparent',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              cursor: 'pointer',
              marginTop: '1rem'
            }}
          >
            {showSuggestions ? 'Hide Suggestions' : 'Show Suggestions'}
          </button>
        )}
      </BioContainer>

      {validationErrors.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          {validationErrors.map((error, index) => (
            <ValidationMessage key={index}>{error}</ValidationMessage>
          ))}
        </div>
      )}

      {bio.content.length >= MIN_CHARACTERS && validationErrors.length === 0 && (
        <BioPreview>
          <h4>Preview:</h4>
          <p>{bio.content}</p>
        </BioPreview>
      )}

      <BioTips>
        <h4>Bio Writing Tips:</h4>
        {BIO_TIPS.map((tip, index) => (
          <TipItem key={index}>{tip}</TipItem>
        ))}
      </BioTips>
    </StepContainer>
  );
};
